import { useRef, useEffect } from "react";
import { Hash, Palette } from "lucide-react";
import {
    useVoronoi,
    type VoronoiFillMode,
    type VoronoiSiteMode,
} from "./VoronoiContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

export function VoronoiControls() {
    const {
        siteCount,
        seed,
        siteMode,
        gridJitter,
        strokeColor,
        strokeWidth,
        fillColor,
        fillMode,
        showSites,
        setSiteCount,
        setSeed,
        setSiteMode,
        setGridJitter,
        setStrokeColor,
        setStrokeWidth,
        setFillColor,
        setFillMode,
        setShowSites,
        randomizeParameters,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useVoronoi();

    const strokeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fillTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (strokeTimeout.current) clearTimeout(strokeTimeout.current);
            if (fillTimeout.current) clearTimeout(fillTimeout.current);
        };
    }, []);

    const handleStrokePicker = (value: string) => {
        if (strokeTimeout.current) clearTimeout(strokeTimeout.current);
        strokeTimeout.current = setTimeout(() => setStrokeColor(value), 150);
    };

    const handleFillPicker = (value: string) => {
        if (fillTimeout.current) clearTimeout(fillTimeout.current);
        fillTimeout.current = setTimeout(() => setFillColor(value), 150);
    };

    const handleInt =
        (setter: (v: number) => void, min = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min) return;
            setter(Math.round(n));
        };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    return (
        <div className="panel-controls voronoi-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Voronoi Diagram</h2>
                    <ToolDescriptionPopover dialogLabel="Voronoi Diagram tool description">
                        <p>
                            Places sites in a rectangle and draws the bounded
                            Voronoi tessellation: each cell contains all points
                            closest to one site.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={randomizeParameters}
                    >
                        Randomize parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="voronoi-diagram"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="Sites" icon={Hash}>
                    <div className="field-row">
                        <label className="field-label">
                            Site count
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={500}
                                step={1}
                                value={siteCount}
                                onChange={handleInt(setSiteCount, 1)}
                                debounceMs={400}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <DebouncedInput
                                type="text"
                                value={seed}
                                onChange={(v) => setSeed(String(v))}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Site placement
                            <select
                                value={siteMode}
                                onChange={(e) =>
                                    setSiteMode(
                                        e.target.value as VoronoiSiteMode,
                                    )
                                }
                            >
                                <option value="random">Random</option>
                                <option value="grid">Grid (+ jitter)</option>
                            </select>
                        </label>
                    </div>

                    {siteMode === "grid" && (
                        <div className="field-row">
                            <label className="field-label">
                                Grid jitter (0-1)
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    value={gridJitter}
                                    onChange={handleFloat(setGridJitter, 0, 1)}
                                    debounceMs={400}
                                />
                            </label>
                        </div>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <div className="field-row">
                        <label className="field-label">
                            Fill mode
                            <select
                                value={fillMode}
                                onChange={(e) =>
                                    setFillMode(
                                        e.target.value as VoronoiFillMode,
                                    )
                                }
                            >
                                <option value="none">None (stroke only)</option>
                                <option value="solid">Solid</option>
                                <option value="perCell">Per-cell hue</option>
                            </select>
                        </label>
                    </div>

                    {fillMode === "solid" && (
                        <div className="field-row">
                            <label className="field-label">
                                Fill color
                                <input
                                    type="color"
                                    value={fillColor}
                                    onChange={(e) =>
                                        handleFillPicker(e.target.value)
                                    }
                                />
                            </label>
                        </div>
                    )}

                    <div className="field-row">
                        <label className="field-label">
                            Stroke color
                            <input
                                type="color"
                                value={strokeColor}
                                onChange={(e) =>
                                    handleStrokePicker(e.target.value)
                                }
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Stroke width
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={8}
                                step={0.25}
                                value={strokeWidth}
                                onChange={handleFloat(setStrokeWidth, 0, 8)}
                                debounceMs={400}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            checked={showSites}
                            onChange={setShowSites}
                            label="Show site dots"
                        />
                    </div>
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
